import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLang, PHONE_TEL, PHONE_DISPLAY } from "@/i18n";

export const Navbar = () => {
    const { lang, setLang, t } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location]);

    const links = [
        { to: "/", label: t("nav.home") },
        { to: "/ercan-transfer", label: t("nav.ercan") },
        { to: "/girne-taksi", label: t("nav.girne") },
        { to: "/#filo", label: t("nav.fleet") },
        { to: "/#fiyatlar", label: t("nav.pricing") },
        { to: "/#iletisim", label: t("nav.contact") },
    ];

    return (
        <header
            data-testid="main-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl border-b ${
                scrolled
                    ? "bg-[#08080A]/90 border-[#D4AF37]/25 py-2"
                    : "bg-[#08080A]/60 border-white/5 py-4"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
                    <Logo size={scrolled ? 36 : 44} />
                    <div className="leading-tight">
                        <span className="ff-accent block text-[#E6C687] text-sm md:text-base tracking-[0.2em]">
                            GİRNE 1
                        </span>
                        <span className="ff-sans block text-[10px] tracking-[0.45em] text-zinc-400">
                            VIP TAKSİ
                        </span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.to.replace(/[\/#]/g, "") || "home"}`}
                            className="ff-sans text-[13px] tracking-[0.14em] uppercase text-zinc-300 hover:text-[#E6C687] transition-colors duration-300"
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <div
                        className="hidden sm:flex items-center rounded-full border border-[#D4AF37]/30 overflow-hidden"
                        data-testid="language-switcher"
                    >
                        {["tr", "en"].map((c) => (
                            <button
                                key={c}
                                onClick={() => setLang(c)}
                                data-testid={`lang-${c}-button`}
                                className={`px-3 py-1.5 text-[11px] tracking-[0.2em] ff-sans transition-colors duration-300 ${
                                    lang === c
                                        ? "bg-[#D4AF37] text-[#08080A] font-semibold"
                                        : "text-zinc-400 hover:text-[#E6C687]"
                                }`}
                            >
                                {c.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    <a
                        href={PHONE_TEL}
                        data-testid="phone-call-button"
                        className="gold-glow-hover hidden md:flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-2 text-[13px] ff-sans text-[#E6C687]"
                    >
                        <Phone size={14} />
                        <span className="tracking-wider">{PHONE_DISPLAY}</span>
                    </a>

                    <button
                        className="lg:hidden text-[#E6C687] p-2"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    className="lg:hidden bg-[#08080A]/95 backdrop-blur-xl border-t border-[#D4AF37]/15 px-6 py-6 flex flex-col gap-5"
                    data-testid="mobile-menu"
                >
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`mobile-nav-${l.to.replace(/[\/#]/g, "") || "home"}`}
                            className="ff-sans text-sm tracking-[0.2em] uppercase text-zinc-200"
                        >
                            {l.label}
                        </Link>
                    ))}
                    <div className="flex items-center gap-3 pt-2">
                        {["tr", "en"].map((c) => (
                            <button
                                key={c}
                                onClick={() => setLang(c)}
                                data-testid={`mobile-lang-${c}-button`}
                                className={`px-4 py-1.5 rounded-full text-xs tracking-[0.2em] border ${
                                    lang === c
                                        ? "border-[#D4AF37] bg-[#D4AF37] text-[#08080A] font-semibold"
                                        : "border-zinc-700 text-zinc-400"
                                }`}
                            >
                                {c.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="mobile-call-button"
                        className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#08080A] font-semibold py-3 ff-sans"
                    >
                        <Phone size={16} /> {PHONE_DISPLAY}
                    </a>
                </div>
            )}
        </header>
    );
};
